import {
  EntityCommonInfoModel,
  FilteredPagedQueryResult,
  FilteredPagedModel,
  CommonMultiDeleteModel,
  EntityReplaceModel,
} from "../../entities/common";
import { ProductModel, ProductSelectModel } from "../../entities/product";
import { apiSlice, TagTypes } from "./apiSlice";
import { getQuerySearchParams } from "../../utils";

export const productApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getProductCommonInfo: builder.query<EntityCommonInfoModel, boolean>({
      query: (isCumulative: boolean) => `/product/commonInfo`,
      providesTags: [{ type: TagTypes.Product, id: "LIST" }],
    }),
    getProductFilteredPaged: builder.query<FilteredPagedQueryResult<ProductModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/product/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.Product, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }: { id: number | string }) => ({
                type: TagTypes.Product,
                id,
              })),
            ]
          : [{ type: TagTypes.Product, id: "PARTIAL-LIST" }],
    }),
    getProduct: builder.query<ProductModel, number>({
      query: (productId) => `/product/${productId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.Product, id: arg }],
    }),
    getProductsSelectFilter: builder.query<ProductSelectModel[], string>({
      query: (filterText: string) => `/product/select/${filterText}`,
      providesTags: (data = [], error, arg) => [
        { type: TagTypes.Product, id: "PARTIAL-LIST" },
        ...data.map(({ id }: { id: number | string }) => ({
          type: TagTypes.Product,
          id,
        })),
      ],
    }),
    addProduct: builder.mutation({
      query: (initialProduct) => ({
        url: "/product",
        method: "POST",
        body: initialProduct,
      }),
      invalidatesTags: [
        { type: TagTypes.Product, id: "LIST" },
        { type: TagTypes.Product, id: "PARTIAL-LIST" },
      ],
    }),
    editProduct: builder.mutation({
      query: (product) => ({
        url: `/product/${product.id}`,
        method: "PUT",
        body: product,
      }),
      invalidatesTags: (result, error, arg) => [{ type: TagTypes.Product, id: arg.id }],
    }),
    deleteProduct: builder.mutation({
      query: (productId) => ({
        url: `/product/${productId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.Product, id: arg },
              { type: TagTypes.Product, id: "LIST" },
            ],
    }),
    deleteMultiProducts: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/product/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.Product,
                id,
              })),
              { type: TagTypes.Product, id: "LIST" },
            ],
    }),
    replaceProduct: builder.mutation({
      query: (replaceModel: EntityReplaceModel) => ({
        url: "/product/replace",
        method: "PUT",
        body: replaceModel,
      }),
      invalidatesTags: [
        { type: TagTypes.Product, id: "LIST" },
        { type: TagTypes.Product, id: "PARTIAL-LIST" },
      ],
    }),
  }),
});

export const {
  useGetProductCommonInfoQuery,
  useGetProductFilteredPagedQuery,
  useGetProductQuery,
  useAddProductMutation,
  useEditProductMutation,
  useDeleteProductMutation,
  useDeleteMultiProductsMutation,
  useReplaceProductMutation,
  useGetProductsSelectFilterQuery,
} = productApi;
